import { appendHelpers, removeHelpers } from '../helpers/helpers_manager'
import { formatDate } from '@/utils/date_manipulation'

const moduleName = '.resources'

export const buildFileFormData = (file, fileKey = 'file', fileNameKey = 'filename') => {
  const formData = new FormData()
  formData.append(fileKey, file)
  formData.append(fileNameKey, file.name)
  return formData
}

const buildRequestForAddImage = (url, data, client) => {
  let req

  if (data.type === 'file') {
    const formData = buildFileFormData(data.payload)
    req = client.api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  }
  else if (data.type === 'url') {
    req = client.api.post(url, { path: data.payload })
  }

  return req
}

const createOrChangeIssueFormDataHeaders = (issue, publicationDate) => {
  let formData = new FormData()
  let headers = {}

  if(issue.cover_file) {
    formData = buildFileFormData(issue.cover_file, 'issue[cover_file]', 'issue[cover_file_name]')
    headers = { 'Content-Type': 'multipart/form-data' }
  }

  formData.append('issue[id]', issue.id)
  formData.append('issue[name]', issue.name)
  formData.append('issue[schedule]', issue.schedule)
  formData.append('issue[publication_date]', formatDate({ date: publicationDate }))
  
  if(issue.iso_code) {
    formData.append('issue[iso_code]', issue.iso_code)
  }

  if(issue.remove_cover) {
    formData.append('issue[remove_cover]', issue.remove_cover)
  }

  return { formData, headers }
}

export default ({ clientWrapper, context, repository }) => ({
  create: (issue, publicationDate, resourceType) => {
    context.lastApiPath = `${moduleName}.create`
    const { formData, headers } = createOrChangeIssueFormDataHeaders(issue, publicationDate)
    return clientWrapper.api.post(`manual_input/${resourceType}`, formData, { headers })
  },

  item: (id) => {
    context.lastApiPath = `${moduleName}.item`
    const helpers = ['issue', 'status', 'revision', 'images', 'rects']

    return clientWrapper.api.get(`/resources/${id}.json`)
      .then(({ data }) => data.attributes)
      .then(publication => appendHelpers(publication, repository, helpers))
  },

  update: (item) => {
    context.lastApiPath = `${moduleName}.update`
    const itemToSend = removeHelpers(item)
    return clientWrapper.api.put(`/resources/${itemToSend.id}`, itemToSend)
  },

  changeIssue: (resource, issue, publicationDate) => {
    context.lastApiPath = `${moduleName}.change_issue`
    const helpers = ['issue', 'status', 'revision', 'images', 'rects']
    const { formData, headers } = createOrChangeIssueFormDataHeaders(issue, publicationDate)

    return clientWrapper.api.put(`/resources/${resource.id}/change_issue`, formData, { headers })
      .then(({ data }) => data.attributes)
      .then(resource => appendHelpers(resource, repository, helpers))
  },

  toggleItemEdit: (itemId, flag = 'stop') => {
    context.lastApiPath = `${moduleName}.toggleItemEdit`
    return clientWrapper.api.put('/resources/' + itemId + `/${flag}_editing`)
  },
  
  discardItem: (itemId) => {
    context.lastApiPath = `${moduleName}.discardItem`
    return clientWrapper.api.put('/resources/' + itemId + '/discard')
  },
  
  draftItem: (itemId) => {
    context.lastApiPath = `${moduleName}.draftItem`
    return clientWrapper.api.put('/resources/' + itemId + '/draft')
  },
  
  enqueueItem: (itemId) => {
    context.lastApiPath = `${moduleName}.enqueueItem`
    return clientWrapper.api.put('/resources/' + itemId + '/enqueue')
  },
  
  itemVersions: (resourceId) => {
    context.lastApiPath = `${moduleName}.itemVersions`
    return clientWrapper.api.get(`/resources/${resourceId}/versions`)
      .then(({ data }) => data.map(pub => pub.attributes))
  },
  
  importImageInPage: (resourceId, pageId, data) => {
    context.lastApiPath = `${moduleName}.importImageInPage`
    const url = `resources/${resourceId}/pages/${pageId}/add_medium`
    const req = buildRequestForAddImage(url, data, clientWrapper)

    return req.then(({ data }) => appendHelpers(data.attributes, repository, ['images']))
              .then(resource => resource.advertorial.pages.find(({ id }) => pageId === id).media)
  },
  
  addPageToResource: (resourceId, data) => {
    context.lastApiPath = `${moduleName}.addPageToResource`
    const url = `resources/${resourceId}/pages`
    const req = buildRequestForAddImage(url, data, clientWrapper)

    return req.then(({ data }) =>
      appendHelpers(data.attributes, repository, ['issue', 'status', 'revision', 'images', 'rects'])
    )
  },

  deletePage: (resourceId, pageId) => {
    context.lastApiPath = `${moduleName}.deletePage`
    const url = `resources/${resourceId}/pages/${pageId}`
    return clientWrapper.api.delete(url).then(({ data }) => (
      appendHelpers(data.attributes, repository, ['issue', 'status', 'revision', 'images', 'rects'])
    ))
  },

  replacePageScreenshot: (resourceId, pageId, data) => {
    context.lastApiPath = `${moduleName}.replacePageScreenshot`
    const url = `resources/${resourceId}/pages/${pageId}/update_screenshot`
    const req = buildRequestForAddImage(url, data, clientWrapper)

    return req.then(({ data }) =>
      appendHelpers(data.attributes, repository, ['issue', 'status', 'revision', 'images', 'rects'])
    )
  },

  forceDownloadPageScreenshot: (resourceId, pageId) => {
    context.lastApiPath = `${moduleName}.forceDownloadPageScreenshot`
    const url = `resources/${resourceId}/pages/${pageId}/redownload_screenshot`

    return clientWrapper.api.put(url)
                            .then(({ data }) =>
                              appendHelpers(data.attributes, repository, ['issue', 'status', 'revision', 'images', 'rects'])
                            )
  },

  destroy: (resourceId) => {
    context.lastApiPath = `${moduleName}.destroy`

    return clientWrapper.api.delete(`resources/${resourceId}`)
  }
})
